import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { apiClient } from "../../lib/api";

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const idx = result.indexOf(",");
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const AdminSiteSettingsPage = () => {
  const queryClient = useQueryClient();
  const settingsQuery = useQuery({
    queryKey: ["site-settings"],
    queryFn: () => apiClient.frontend.GetSiteSettings(),
  });

  const [aboutText, setAboutText] = useState("");
  const [textTouched, setTextTouched] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Keep the textarea synced with server state until the admin starts editing.
  useEffect(() => {
    if (settingsQuery.data && !textTouched) {
      setAboutText(settingsQuery.data.AboutText);
    }
  }, [settingsQuery.data, textTouched]);

  const textMutation = useMutation({
    mutationFn: () =>
      apiClient.frontend.UpdateSiteSettings({ AboutText: aboutText }),
    onSuccess: (data) => {
      queryClient.setQueryData(["site-settings"], data);
      setTextTouched(false);
    },
  });

  const imageMutation = useMutation({
    mutationFn: async (file: File) => {
      const data = await fileToBase64(file);
      return apiClient.frontend.UpdateAboutImage({
        ContentType: file.type,
        DataBase64: data,
      });
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["site-settings"], data);
      setImageError(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    onError: (err) => {
      setImageError(err instanceof Error ? err.message : "Image upload failed.");
    },
  });

  const resetImageMutation = useMutation({
    mutationFn: () => apiClient.frontend.ResetAboutImage(),
    onSuccess: (data) => {
      queryClient.setQueryData(["site-settings"], data);
      setImageError(null);
    },
  });

  const currentImage = settingsQuery.data?.AboutImageURL || "/artist.jpg";
  const usingCustomImage = !!settingsQuery.data?.AboutImageURL;

  return (
    <div>
      <h1 className="font-display text-3xl uppercase-track mb-2">Site</h1>
      <p className="text-xs uppercase-track text-[var(--color-kj-text-light)] mb-10">
        Content shown on the public homepage
      </p>

      {settingsQuery.isLoading && (
        <p className="text-sm text-[var(--color-kj-text-light)]">Loading…</p>
      )}

      {settingsQuery.isError && (
        <p className="text-sm text-[var(--color-kj-danger)]">
          Failed to load settings.
        </p>
      )}

      {settingsQuery.isSuccess && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* About text */}
          <section>
            <h2 className="font-display text-xl uppercase-track mb-4">
              About the Artist · Text
            </h2>
            <textarea
              value={aboutText}
              onChange={(e) => {
                setAboutText(e.target.value);
                setTextTouched(true);
              }}
              rows={10}
              className="w-full bg-transparent border border-[var(--color-kj-border)] focus:border-[var(--color-kj-text)] outline-none p-4 text-sm leading-relaxed resize-y transition-colors"
            />
            <p className="mt-2 text-[10px] uppercase-track text-[var(--color-kj-text-light)]">
              Line breaks are preserved on the public site.
            </p>
            <div className="mt-4 flex items-center gap-4">
              <button
                type="button"
                disabled={textMutation.isPending || !textTouched || aboutText.trim() === ""}
                onClick={() => textMutation.mutate()}
                className="font-display uppercase-track text-xs px-6 py-2.5 border border-[var(--color-kj-text)] hover:bg-[var(--color-kj-text)] hover:text-[var(--color-kj-bg)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {textMutation.isPending ? "Saving…" : "Save text"}
              </button>
              {textTouched && (
                <button
                  type="button"
                  onClick={() => {
                    setAboutText(settingsQuery.data?.AboutText ?? "");
                    setTextTouched(false);
                  }}
                  className="text-xs uppercase-track text-[var(--color-kj-text-light)] hover:text-[var(--color-kj-text)] cursor-pointer"
                >
                  Discard
                </button>
              )}
              {textMutation.isSuccess && !textTouched && (
                <p className="text-xs uppercase-track text-[var(--color-kj-accent)]">
                  Saved.
                </p>
              )}
              {textMutation.isError && (
                <p className="text-xs uppercase-track text-[var(--color-kj-danger)]">
                  Save failed.
                </p>
              )}
            </div>
          </section>

          {/* About image */}
          <section>
            <h2 className="font-display text-xl uppercase-track mb-4">
              About the Artist · Image
            </h2>
            <div className="border border-[var(--color-kj-border)] inline-block">
              <img
                src={currentImage}
                alt="Current artist photo"
                className="block w-full max-w-xs aspect-[4/5] object-cover"
              />
            </div>
            <p className="mt-2 text-[10px] uppercase-track text-[var(--color-kj-text-light)]">
              {usingCustomImage ? "Custom upload" : "Using the default photo"}
            </p>

            <div className="mt-6">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                disabled={imageMutation.isPending}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) imageMutation.mutate(f);
                }}
                className="block w-full text-xs disabled:opacity-50 disabled:cursor-not-allowed"
              />
              {imageMutation.isPending && (
                <p className="mt-2 text-xs uppercase-track text-[var(--color-kj-text-light)]">
                  Uploading…
                </p>
              )}
              {imageError && (
                <p className="mt-2 text-xs uppercase-track text-[var(--color-kj-danger)]">
                  {imageError}
                </p>
              )}
            </div>

            {usingCustomImage && (
              <div className="mt-4">
                <button
                  type="button"
                  disabled={resetImageMutation.isPending}
                  onClick={() => {
                    if (confirm("Revert to the default photo?")) {
                      resetImageMutation.mutate();
                    }
                  }}
                  className="text-xs uppercase-track text-[var(--color-kj-text-light)] hover:text-[var(--color-kj-text)] disabled:opacity-50 cursor-pointer"
                >
                  {resetImageMutation.isPending ? "Reverting…" : "Revert to default"}
                </button>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
};

export default AdminSiteSettingsPage;
